import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import {
  SUPPORTED_LANGUAGES,
  SUPPORTED_CODES,
  DEFAULT_LANGUAGE,
  getLangFromPath,
  stripLangFromPath,
  localizePath,
} from '../i18n';

const SITE_URL = 'https://negocios.moment-ia.shop';
const DEFAULT_IMAGE = `${SITE_URL}/og-cover.jpg`;

const OG_LOCALES = {
  es: 'es_AR',
  en: 'en_US',
  it: 'it_IT',
  pt: 'pt_BR',
};

/**
 * Per-page SEO component.
 * Emits <title>, meta description, canonical, hreflang alternates,
 * OG/Twitter cards and optional JSON-LD schemas.
 *
 * The `path` prop is the untranslated route (e.g. `/precios`) — hreflang
 * alternates are generated for every supported language automatically.
 */
export default function SEO({
  title,
  description,
  path = '/',
  image = DEFAULT_IMAGE,
  type = 'website',
  schema,
  noindex = false,
}) {
  const location = useLocation();
  const currentLang = getLangFromPath(location?.pathname || '/');
  const basePath = stripLangFromPath(path);
  const canonical = `${SITE_URL}${localizePath(basePath, currentLang)}`;
  const fullTitle = title.includes('momentIA') ? title : `${title} · momentIA`;

  return (
    <Helmet prioritizeSeoTags>
      <html lang={currentLang} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* hreflang alternates (one per supported language) */}
      {SUPPORTED_CODES.map((code) => (
        <link
          key={code}
          rel="alternate"
          hrefLang={code}
          href={`${SITE_URL}${localizePath(basePath, code)}`}
        />
      ))}
      <link
        rel="alternate"
        hrefLang="x-default"
        href={`${SITE_URL}${localizePath(basePath, DEFAULT_LANGUAGE)}`}
      />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="momentIA" />
      <meta property="og:locale" content={OG_LOCALES[currentLang] || OG_LOCALES.es} />
      {SUPPORTED_CODES
        .filter((c) => c !== currentLang)
        .map((c) => (
          <meta key={`og-alt-${c}`} property="og:locale:alternate" content={OG_LOCALES[c]} />
        ))}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
    </Helmet>
  );
}

// Reusable JSON-LD helpers
export const serviceSchema = ({ name, description, path, area = 'AR' }) => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  serviceType: name,
  provider: {
    '@type': 'Organization',
    name: 'momentIA',
    url: SITE_URL,
  },
  areaServed: area,
  description,
  url: `${SITE_URL}${path}`,
});

export const breadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((it, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: it.name,
    item: `${SITE_URL}${it.path}`,
  })),
});

export const faqSchema = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
});

// Export supported languages for other components
export { SUPPORTED_LANGUAGES };
